import { jsPDF } from "jspdf";

export type ImageGeneratorDownloadFormat = "png" | "jpeg" | "svg" | "pdf";

const DOWNLOAD_REVOKE_MS = 2000;

const triggerDownload = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const revokeLater = (url: string) => {
  window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_MS);
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image."));
    image.src = src;
  });

const fetchImageBlob = async (previewUrl: string, fallback: string) => {
  const source = await fetch(previewUrl);
  if (!source.ok) {
    throw new Error(fallback);
  }
  return source.blob();
};

const rasterizeBlob = async (blob: Blob, background?: string) => {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to convert this image.");
    }
    if (background) {
      context.fillStyle = background;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      next => {
        if (!next) {
          reject(new Error("Unable to convert this image."));
          return;
        }
        resolve(next);
      },
      type,
      quality
    );
  });

const canvasToPdfBlob = async (canvases: HTMLCanvasElement[]) => {
  if (!canvases.length) {
    throw new Error("Unable to build PDF.");
  }
  let doc: jsPDF | null = null;
  for (const canvas of canvases) {
    const width = canvas.width || 1;
    const height = canvas.height || 1;
    const orientation = width >= height ? "landscape" : "portrait";
    const dataUrl = canvas.toDataURL("image/png");
    if (!doc) {
      doc = new jsPDF({
        orientation,
        unit: "px",
        format: [width, height],
        hotfixes: ["px_scaling"],
      });
    } else {
      doc.addPage([width, height], orientation);
    }
    doc.addImage(dataUrl, "PNG", 0, 0, width, height);
  }
  return doc!.output("blob");
};

export const downloadImageGeneratorLook = async (
  previewUrl: string,
  fileName: string,
  format: ImageGeneratorDownloadFormat
) => {
  const blob = await fetchImageBlob(
    previewUrl,
    "Unable to download this image."
  );
  if (format === "png" && blob.type === "image/png") {
    const objectUrl = URL.createObjectURL(blob);
    triggerDownload(objectUrl, `${fileName}.png`);
    revokeLater(objectUrl);
    return;
  }

  // Structured SVG (e.g. moodboard svg_hybrid): download as-is before any rasterize.
  // Raster / AI boards still wrap the PNG in a single <image> (not editable vectors).
  if (format === "svg") {
    const urlLooksSvg = /\.svg(\?|#|$)/i.test(previewUrl);
    const typeLooksSvg = /svg/i.test(blob.type || "");
    // Always sniff markup for SVG downloads. CloudFront may serve octet-stream /
    // wrong Content-Type; size alone must not skip reading a real SVG.
    let markup = "";
    try {
      const head = await blob.slice(0, 8192).text();
      if (
        /<svg[\s>]/i.test(head) ||
        /<\?xml[\s\S]{0,200}<svg[\s>]/i.test(head)
      ) {
        markup = await blob.text();
      }
    } catch {
      markup = "";
    }
    const isStructuredSvg =
      Boolean(markup) &&
      /<svg[\s>]/i.test(markup) &&
      // Live <text> / groups = structured board; never re-wrap as PNG.
      (/<text[\s>]/i.test(markup) ||
        /<g[\s>]/i.test(markup) ||
        /<image[\s>]/i.test(markup));
    if (urlLooksSvg || typeLooksSvg || isStructuredSvg) {
      const svgBlob = markup
        ? new Blob([markup], { type: "image/svg+xml;charset=utf-8" })
        : typeLooksSvg
          ? blob
          : new Blob([await blob.arrayBuffer()], {
              type: "image/svg+xml;charset=utf-8",
            });
      const objectUrl = URL.createObjectURL(svgBlob);
      triggerDownload(objectUrl, `${fileName}.svg`);
      revokeLater(objectUrl);
      return;
    }
  }

  const canvas = await rasterizeBlob(
    blob,
    format === "jpeg" || format === "pdf" ? "#ffffff" : undefined
  );
  if (format === "jpeg") {
    const jpeg = await canvasToBlob(canvas, "image/jpeg", 0.92);
    const jpegUrl = URL.createObjectURL(jpeg);
    triggerDownload(jpegUrl, `${fileName}.jpeg`);
    revokeLater(jpegUrl);
    return;
  }
  if (format === "png") {
    const png = await canvasToBlob(canvas, "image/png");
    const pngUrl = URL.createObjectURL(png);
    triggerDownload(pngUrl, `${fileName}.png`);
    revokeLater(pngUrl);
    return;
  }
  if (format === "pdf") {
    const pdfBlob = await canvasToPdfBlob([canvas]);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    triggerDownload(pdfUrl, `${fileName}.pdf`);
    revokeLater(pdfUrl);
    return;
  }

  const png = canvas.toDataURL("image/png");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}"><image href="${png}" width="${canvas.width}" height="${canvas.height}"/></svg>`;
  const svgUrl = URL.createObjectURL(
    new Blob([svg], { type: "image/svg+xml;charset=utf-8" })
  );
  triggerDownload(svgUrl, `${fileName}.svg`);
  revokeLater(svgUrl);
};

/**
 * Build one PDF from one or more look/print previews (e.g. front + back for Full Look / Denim).
 */
export const downloadImageGeneratorLookPdf = async (
  pages: Array<{ previewUrl: string; label?: string }>,
  fileName: string
) => {
  const ready = pages.filter(page => page.previewUrl);
  if (!ready.length) {
    throw new Error("Unable to download this image.");
  }
  const canvases: HTMLCanvasElement[] = [];
  for (const page of ready) {
    const blob = await fetchImageBlob(
      page.previewUrl,
      "Unable to download this image."
    );
    canvases.push(await rasterizeBlob(blob, "#ffffff"));
  }
  const pdfBlob = await canvasToPdfBlob(canvases);
  const pdfUrl = URL.createObjectURL(pdfBlob);
  triggerDownload(pdfUrl, `${fileName}.pdf`);
  revokeLater(pdfUrl);
};

/** Downloads every look/slide in the chosen format (sequential browser downloads). */
export const downloadImageGeneratorLooks = async (
  items: Array<{ previewUrl: string; fileName: string }>,
  format: ImageGeneratorDownloadFormat
) => {
  const ready = items.filter(item => item.previewUrl);
  if (format === "pdf") {
    await downloadImageGeneratorLookPdf(
      ready.map(item => ({ previewUrl: item.previewUrl })),
      ready[0]?.fileName || "image-generator"
    );
    return;
  }
  for (let index = 0; index < ready.length; index += 1) {
    const item = ready[index];
    await downloadImageGeneratorLook(item.previewUrl, item.fileName, format);
    if (index < ready.length - 1) {
      await new Promise(resolve => window.setTimeout(resolve, 280));
    }
  }
};

export const copyImageGeneratorLook = async (previewUrl: string) => {
  if (!navigator.clipboard?.write || !window.ClipboardItem) {
    throw new Error("Copy is not supported in this browser.");
  }
  const blob = await fetchImageBlob(previewUrl, "Unable to copy this image.");
  const pngBlob =
    blob.type === "image/png"
      ? blob
      : await canvasToBlob(await rasterizeBlob(blob), "image/png");
  await navigator.clipboard.write([
    new ClipboardItem({ "image/png": pngBlob }),
  ]);
};
